import { logger } from '../logger';
import { compress, decompress } from './compression';

interface CacheItem<T> {
  data: T;
  timestamp: number;
  lastAccessed: number;
  priority: number;
  size: number;
  compressed: boolean;
}

interface CacheConfig {
  ttl: number;
  maxSize: number;
  maxItems: number;
  cleanupInterval: number;
  compressionThreshold: number;
  highPriorityTTL: number;
  maxMemoryUsage: number;
}

type Priority = 'low' | 'medium' | 'high';

export class CacheManager {
  private static instance: CacheManager;
  private cache: Map<string, CacheItem<any>>;
  private config: CacheConfig;
  private cleanupTimer: number | null = null;
  private totalSize: number = 0;
  private preloadQueue: Set<string> = new Set();

  private constructor() {
    this.cache = new Map();
    this.config = {
      ttl: 5 * 60 * 1000, // 5 minutes par défaut
      maxSize: 50 * 1024 * 1024, // 50MB
      maxItems: 1000,
      cleanupInterval: 60 * 1000, // 1 minute
      compressionThreshold: 1024, // 1KB
      highPriorityTTL: 30 * 60 * 1000, // 30 minutes
      maxMemoryUsage: 0.8 // 80% de la mémoire disponible
    };
    this.startCleanupTimer();
    this.monitorMemoryUsage();
  }

  public static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  private startCleanupTimer(): void {
    if (this.cleanupTimer) {
      window.clearInterval(this.cleanupTimer);
    }
    this.cleanupTimer = window.setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  private monitorMemoryUsage(): void {
    if ('performance' in window && 'memory' in performance) {
      setInterval(() => {
        const memoryInfo = (performance as any).memory;
        const usageRatio = memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit;
        
        if (usageRatio > this.config.maxMemoryUsage) {
          logger.warn('High memory usage detected, cleaning cache', {
            category: 'Cache',
            data: {
              usageRatio,
              totalSize: this.totalSize,
              itemCount: this.cache.size
            }
          });
          this.cleanup(true);
        }
      }, 30000); // Vérifier toutes les 30 secondes
    }
  }

  private calculateItemSize(item: any): number {
    try {
      const str = JSON.stringify(item);
      return str.length * 2; // Approximation de la taille en bytes
    } catch {
      return 0;
    }
  }

  private shouldCompress(size: number): boolean {
    return size > this.config.compressionThreshold;
  }

  private cleanup(aggressive: boolean = false): void {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());
    
    // Trier par priorité (décroissante) et date de dernier accès (croissante)
    entries.sort(([, a], [, b]) => {
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      return a.lastAccessed - b.lastAccessed;
    });

    let itemsToRemove: string[] = [];

    // Identifier les éléments à supprimer
    for (const [key, item] of entries) {
      const isExpired = now > item.timestamp;
      const isLowPriority = item.priority === 0;
      
      if (isExpired || (aggressive && isLowPriority)) {
        itemsToRemove.push(key);
        this.totalSize -= item.size;
      }
    }

    // Si on dépasse toujours la taille maximale, supprimer les éléments les moins utilisés
    if (this.totalSize > this.config.maxSize || this.cache.size > this.config.maxItems) {
      for (const [key, item] of entries) {
        if (!itemsToRemove.includes(key)) {
          itemsToRemove.push(key);
          this.totalSize -= item.size;
          
          if (this.totalSize <= this.config.maxSize && this.cache.size <= this.config.maxItems) {
            break;
          }
        }
      }
    }

    // Supprimer les éléments
    itemsToRemove.forEach(key => {
      this.cache.delete(key);
      logger.debug(`Cache item removed: ${key}`, { category: 'Cache' });
    });
  }

  public set<T>(
    key: string, 
    data: T, 
    ttl?: number,
    priority: Priority = 'low'
  ): void {
    // Nettoyer si nécessaire avant d'ajouter
    if (this.cache.size >= this.config.maxItems) {
      this.cleanup();
    }

    const size = this.calculateItemSize(data);
    let itemData = data;
    let isCompressed = false;

    // Compresser si nécessaire
    if (this.shouldCompress(size)) {
      itemData = compress(data);
      isCompressed = true;
    }

    const priorityValue = priority === 'high' ? 2 : priority === 'medium' ? 1 : 0;
    const itemTTL = priority === 'high' ? 
      (ttl || this.config.highPriorityTTL) : 
      (ttl || this.config.ttl);

    this.cache.set(key, {
      data: itemData,
      timestamp: Date.now() + itemTTL,
      lastAccessed: Date.now(),
      priority: priorityValue,
      size,
      compressed: isCompressed
    });

    this.totalSize += size;

    logger.debug(`Cache item set: ${key}`, {
      category: 'Cache',
      data: { size, priority, compressed: isCompressed }
    });
  }

  public get<T>(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) return null;

    const now = Date.now();
    if (now > item.timestamp) {
      this.cache.delete(key);
      return null;
    }

    // Mettre à jour le timestamp de dernier accès
    item.lastAccessed = now;

    // Décompresser si nécessaire
    const data = item.compressed ? decompress(item.data) : item.data;

    logger.debug(`Cache hit: ${key}`, {
      category: 'Cache',
      data: { compressed: item.compressed }
    });

    return data as T;
  }

  public preload<T>(key: string, loader: () => Promise<T>, priority: Priority = 'low'): Promise<T> {
    // Éviter les chargements en double
    if (this.preloadQueue.has(key)) {
      logger.debug(`Preload already in progress: ${key}`, { category: 'Cache' });
      return Promise.reject(new Error('Preload already in progress'));
    }

    this.preloadQueue.add(key);

    return new Promise(async (resolve, reject) => {
      try {
        // Vérifier d'abord le cache
        const cached = this.get<T>(key);
        if (cached) {
          resolve(cached);
          return;
        }

        // Charger les données
        const data = await loader();
        this.set(key, data, undefined, priority);
        resolve(data);
      } catch (error) {
        reject(error);
      } finally {
        this.preloadQueue.delete(key);
      }
    });
  }

  public has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;
    return Date.now() <= item.timestamp;
  }

  public clear(): void {
    this.cache.clear();
    this.totalSize = 0;
    this.preloadQueue.clear();
    logger.info('Cache cleared', { category: 'Cache' });
  }

  public getStats(): {
    size: number;
    maxSize: number;
    itemCount: number;
    maxItems: number;
    memoryUsage: number;
  } {
    const memoryInfo = (performance as any).memory;
    const memoryUsage = memoryInfo ? 
      memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit : 
      0;

    return {
      size: this.totalSize,
      maxSize: this.config.maxSize,
      itemCount: this.cache.size,
      maxItems: this.config.maxItems,
      memoryUsage
    };
  }
}

export const cacheManager = CacheManager.getInstance();